import { type CSSProperties, type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import type { JSONContent } from '@tiptap/core';

import EmblaCarousel from '@/components/EmblaCarousel';
import { ContainerElement } from '@/containerElement';
import { Heading3Element } from '@/headingElement';
import { ParagraphElement } from '@/paragraphElement';
import SearchInput from '@/postElement/pagination/SearchInput';
import type { SerializableNode } from '@/serializer';
import { SerializeTextLeaf } from '@/textLeaf';
import type { RSSFeedDataYouTube, RSSFeedDataYouTubeEntry } from '@/types';
import { EmptyState } from '@/ui/EmptyState/EmptyState';
import { getBackgroundColorStyle } from '@/utils/background';
import { cn } from '@/utils/cn';
import { useMobileCarouselSettings } from '@/utils/useMobileCarouselSettings';

import { Dialog, DialogContent, DialogOverlay, DialogPortal } from '../ui/Dialog';

import * as styles from './RSSFeed.css';
import { isYouTubeShort, type RSSFeedElementProps } from './RSSFeed.types';
import { PodcastSerializer } from './Serializer';
import {
  buildYouTubeThumbnailSizes,
  buildYouTubeThumbnailSrcSet,
  buildYouTubeThumbnailUrl,
  formatDate,
  getYouTubeThumbnailBaseUrl,
  getYouTubeThumbnailSize,
} from './util';
import { YouTubePlayButton } from './YouTubePlayButton';

const YOUTUBE_RSS_FEED_DEFAULTS = {
  imageBorderRadius: '8px',
  titleFontSize: 24,
  titleFontFamily: 'Inter',
  subtitleFontSize: 14,
  subtitleFontFamily: 'Inter',
  titleTextColor: 'inherit',
  subtitleTextColor: 'inherit',
  titleFontWeight: 700,
  subtitleFontWeight: 400,

  borderRadius: '8px',
  borderWidth: '1px',
  borderColor: 'inherit',
  borderStyle: 'none',
  background: 'transparent',
};

const COLUMNS_MAP: Record<string, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
  '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  '5': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
  '6': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6',
};

const OPTIONS: any = {
  align: 'start',
  containScroll: 'trimSnaps',
};

export const YouTubeRssFeedCard = (
  props: Omit<RSSFeedElementProps, 'children'> & {
    entry: RSSFeedDataYouTubeEntry;
    onOpenModal: (videoId: string) => void;
  },
) => {
  const { entry, element, isPreviewing, onOpenModal, isLive = true } = props;
  const {
    itemPadding,
    itemGap,
    imageBorderRadius,
    imageAspectRatio,
    imageFit,
    titleFontSize,
    titleFontFamily,
    titleTextColor,
    titleFontWeight,
    titleAlignment,
    titleLineClamp,
    subtitleFontSize,
    subtitleFontFamily,
    subtitleTextColor,
    subtitleFontWeight,
    subtitleAlignment,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    backgroundColor,
    textGap,
    showPlayButton,
    playAction,
    hideDate,

    // Card structure
    structureTiptapState,
    hasMigratedToCardStructure,
  } = element.attrs;

  const rssThumbnailUrl = entry['media:group']?.['media:thumbnail']?.['@url'];
  const rssThumbnailWidth = Number.parseInt(entry['media:group']?.['media:thumbnail']?.['@width'] ?? '', 10);
  const thumbnailBaseUrl = getYouTubeThumbnailBaseUrl(rssThumbnailUrl);
  const thumbnailSize = getYouTubeThumbnailSize(rssThumbnailUrl, rssThumbnailWidth);
  const thumbnailUrl = thumbnailBaseUrl ? buildYouTubeThumbnailUrl(thumbnailBaseUrl, thumbnailSize) : rssThumbnailUrl;
  const thumbnailSrcSet = thumbnailBaseUrl ? buildYouTubeThumbnailSrcSet(thumbnailBaseUrl, thumbnailSize) : undefined;
  const thumbnailSizes = buildYouTubeThumbnailSizes(element.attrs.columns);

  const handleClick = (videoId: string) => (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (isPreviewing || !isLive) {
      return;
    }

    if (playAction === 'modal') {
      e.preventDefault();
      e.stopPropagation();
      onOpenModal(videoId);
    }
    // If type link, it's already handled by the anchor tag
  };

  if (hasMigratedToCardStructure && structureTiptapState) {
    const cardNode = structureTiptapState?.content?.[0] as JSONContent & SerializableNode;

    return (
      <div
        key={entry.id}
        className={cn('embla__slide relative podcast-serializer-container', isLive && 'cursor-pointer')}
      >
        {isLive ? (
          <a
            href={entry.link?.['@href']}
            target="_blank"
            rel="noopener noreferrer"
            className="relative z-10 h-full embla__slide__number"
            onClick={handleClick(entry['yt:videoId'])}
          >
            <PodcastSerializer
              key={entry.id}
              node={cardNode}
              rootClassName="h-full"
              rootStyle={{}}
              episodeTitle={entry.title}
              episodeUrl={entry.link?.['@href']}
              episodePublished={formatDate(entry.published)}
              episodeThumbnailUrl={thumbnailUrl}
              episodeThumbnailSrcSet={thumbnailSrcSet}
              episodeThumbnailSizes={thumbnailSizes}
              isLive={isLive}
              showPlayButton={showPlayButton}
            />
          </a>
        ) : (
          <PodcastSerializer
            key={entry.id}
            node={cardNode}
            rootClassName="h-full"
            rootStyle={{}}
            episodeTitle={entry.title}
            episodeUrl={entry.link?.['@href']}
            episodePublished={formatDate(entry.published)}
            episodeThumbnailUrl={thumbnailUrl}
            episodeThumbnailSrcSet={thumbnailSrcSet}
            episodeThumbnailSizes={thumbnailSizes}
            isLive={isLive}
            showPlayButton={showPlayButton}
          />
        )}
      </div>
    );
  }

  return (
    <a
      href={entry.link?.['@href']}
      key={entry.id}
      className={cn(
        'flex flex-col items-start cursor-pointer embla__slide relative',
        styles.youtubeCard,
        isPreviewing && 'pointer-events-none',
      )}
      target="_blank"
      rel="noopener noreferrer"
      style={
        {
          padding: itemPadding,
          gap: itemGap || '8px',

          '--card-border-radius': borderRadius || YOUTUBE_RSS_FEED_DEFAULTS.borderRadius,
          '--card-border-width': borderWidth || YOUTUBE_RSS_FEED_DEFAULTS.borderWidth,
          '--card-border-color': borderColor || YOUTUBE_RSS_FEED_DEFAULTS.borderColor,
          '--card-border-style': borderStyle || YOUTUBE_RSS_FEED_DEFAULTS.borderStyle,
          ...getBackgroundColorStyle(backgroundColor),
        } as CSSProperties
      }
    >
      <ContainerElement
        element={{
          type: 'container',
          attrs: {
            id: 'image-container',
            padding: '0px',
            position: 'relative',
            backgroundColor: 'transparent',
            height: 'auto',
            borderRadius: imageBorderRadius || YOUTUBE_RSS_FEED_DEFAULTS.imageBorderRadius,
            overflow: 'hidden',
          },
          content: [],
        }}
        onClick={handleClick(entry['yt:videoId'])}
      >
        <img
          src={thumbnailUrl}
          srcSet={thumbnailSrcSet}
          sizes={thumbnailSizes}
          alt="YouTube Thumbnail"
          className="w-full h-auto"
          style={{
            minWidth: '120px',
            minHeight: '70px',
            aspectRatio: imageAspectRatio || '16/9',
            objectFit: imageFit || 'cover',
          }}
        />
        {showPlayButton && <YouTubePlayButton />}
      </ContainerElement>
      <ContainerElement
        element={{
          type: 'container',
          attrs: {
            id: 'container',
            padding: '0px',
            backgroundColor: 'transparent',
            gap: textGap || '8px',
          },
          content: [],
        }}
      >
        <Heading3Element
          element={{
            type: 'heading',
            attrs: {
              id: 'heading3',
              anchorId: 'heading3',
              padding: '0px',
              anchorTitle: 'heading3',
              anchorIdSync: true,
              anchorEnabled: true,
              anchorTitleSync: true,
              customAttributes: {},
              textAlign: titleAlignment || 'left',
              level: 3,
            },
            content: [
              {
                text: entry.title,
              },
            ],
          }}
          className={titleLineClamp ? `${titleLineClamp} text-ellipsis` : ''}
        >
          <SerializeTextLeaf
            leaf={{
              text: entry.title,
              marks: [
                {
                  type: 'textStyle',
                  attrs: {
                    fontSize: titleFontSize || YOUTUBE_RSS_FEED_DEFAULTS.titleFontSize,
                    fontFamily: titleFontFamily || YOUTUBE_RSS_FEED_DEFAULTS.titleFontFamily,
                    color: titleTextColor || YOUTUBE_RSS_FEED_DEFAULTS.titleTextColor,
                    fontWeight: titleFontWeight || YOUTUBE_RSS_FEED_DEFAULTS.titleFontWeight,
                  },
                },
              ],
            }}
          />
        </Heading3Element>
        {!hideDate && (
          <ParagraphElement
            element={{
              type: 'paragraph',
              attrs: {
                textAlign: subtitleAlignment || 'left',
                padding: '0px',
              },
            }}
          >
            <SerializeTextLeaf
              leaf={{
                text: formatDate(entry.published),
                marks: [
                  {
                    type: 'textStyle',
                    attrs: {
                      fontSize: subtitleFontSize || YOUTUBE_RSS_FEED_DEFAULTS.subtitleFontSize,
                      fontFamily: subtitleFontFamily || YOUTUBE_RSS_FEED_DEFAULTS.subtitleFontFamily,
                      color: subtitleTextColor || YOUTUBE_RSS_FEED_DEFAULTS.subtitleTextColor,
                      fontWeight: subtitleFontWeight || YOUTUBE_RSS_FEED_DEFAULTS.subtitleFontWeight,
                    },
                  },
                ],
              }}
            />
          </ParagraphElement>
        )}
      </ContainerElement>
    </a>
  );
};

export const YouTubeRssFeed = (props: PropsWithChildren<RSSFeedElementProps>) => {
  const { element, attributes, children, className, isPreviewing, isLive, viewport } = props;
  const {
    url,
    data: rawData,
    gap,
    padding,
    isScrollHorizontally,
    isMobileScrollHorizontally,
    rssSelectionType,
    selectedRSSItems,
    rssOffset,
    youtubeVideoFilter,

    // Search Input
    inputBackgroundColor,
    inputTextColor,
    inputPlaceholderColor,
    inputBorderRadius,
    inputBorderWidth,
    inputBorderColor,
    inputBorderStyle,
    inputEnabled,
    inputBorderFocus,
    inputIconFill,
    inputAlignment,
    inputMaxWidth,
    inputMaxHeight,
    inputMarginBottom,

    // Empty State
    emptyStatePadding,
    emptyStateBackgroundColor,
    emptyStateTextColor,
    emptyStateText,
    emptyStateFontSize,
  } = element.attrs;

  const isViewportMobile = viewport === 'mobile';

  const {
    columns: effectiveColumns,
    shouldScrollHorizontally: displayedShouldScrollHorizontally,
    sliderControlsDisplayType: displayedSliderControlsDisplayType,
    sliderControlsBackgroundColor: displayedSliderControlsBackgroundColor,
    sliderControlsTextColor: displayedSliderControlsTextColor,
    sliderControlsBorderColor: displayedSliderControlsBorderColor,
  } = useMobileCarouselSettings({
    attributes: {
      ...element.attrs,
      shouldScrollHorizontally: isScrollHorizontally,
      mobileShouldScrollHorizontally: isMobileScrollHorizontally,
    },
    isViewportMobile: isViewportMobile || false,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalVideoId, setModalVideoId] = useState('');
  const showCount = element.attrs.showCount || 5;

  const data = rawData as RSSFeedDataYouTube;

  // If using free selection with selected items, skip data validation
  const hasSelectedItems = rssSelectionType === 'free_selection' && selectedRSSItems && selectedRSSItems.length > 0;

  const desktopSlideSize =
    displayedShouldScrollHorizontally && effectiveColumns ? `${100 / Number.parseInt(effectiveColumns)}%` : '';

  const offset = rssOffset || 0;

  const filterByVideoType = useCallback(
    (entries: RSSFeedDataYouTubeEntry[]): RSSFeedDataYouTubeEntry[] => {
      if (!youtubeVideoFilter || youtubeVideoFilter === 'all') return entries;
      if (youtubeVideoFilter === 'videos_only') return entries.filter((e) => !isYouTubeShort(e));
      if (youtubeVideoFilter === 'shorts_only') return entries.filter((e) => isYouTubeShort(e));
      return entries;
    },
    [youtubeVideoFilter],
  );

  // Use selected items for free selection, otherwise use latest from feed with offset
  const defaultShownData = useMemo(() => {
    if (hasSelectedItems) {
      return filterByVideoType(selectedRSSItems as RSSFeedDataYouTubeEntry[]);
    }
    if (Array.isArray(data?.feed?.entry)) {
      return filterByVideoType(data.feed.entry).slice(offset, offset + showCount);
    }
    if (data?.feed?.entry) {
      return filterByVideoType([data.feed.entry]);
    }
    return [];
  }, [hasSelectedItems, selectedRSSItems, data?.feed?.entry, offset, showCount, filterByVideoType]);

  const [shownData, setShownData] = useState(defaultShownData);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    setShownData(defaultShownData);
  }, [defaultShownData]);

  const onSearchQueryChange = useCallback(
    (query: string) => {
      if (!query) {
        setShownData(defaultShownData);
        return;
      }
      const lowerQuery = query.toLowerCase();
      if (hasSelectedItems) {
        const items = filterByVideoType(selectedRSSItems as RSSFeedDataYouTubeEntry[]);
        const filtered = items.filter((item) => {
          return item.title?.toLowerCase().includes(lowerQuery);
        });
        setShownData(filtered);
        return;
      }

      const items = Array.isArray(data?.feed?.entry) ? data.feed.entry : data?.feed?.entry ? [data.feed.entry] : [];
      const filtered = filterByVideoType(items)
        .filter((item) => {
          return item.title?.toLowerCase().includes(lowerQuery);
        })
        ?.slice(offset, offset + showCount);
      setShownData(filtered);
    },
    [defaultShownData, hasSelectedItems, filterByVideoType, selectedRSSItems, data?.feed?.entry, offset, showCount],
  );

  if (!hasSelectedItems && !data?.feed?.entry) {
    return (
      <div className={cn('grid relative w-full h-fit', className)} {...attributes}>
        <p className="text-sm text-gray-900 text-left leading-tight">YouTube feed not found</p>
        <p className="text-xs text-gray-500 text-left leading-tight">{url}</p>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn('relative flex flex-col w-full', !isLive && 'pointer-events-none')}
        style={
          {
            // Search Input
            '--input-background-color': inputBackgroundColor,
            '--input-text-color': inputTextColor,
            '--input-placeholder-color': inputPlaceholderColor,
            '--input-border-radius': inputBorderRadius,
            '--input-border-width': inputBorderWidth,
            '--input-border-color': inputBorderColor,
            '--input-border-style': inputBorderStyle,
            '--input-border-focus': inputBorderFocus,
            '--input-icon-fill': inputIconFill,
            '--input-max-width': inputMaxWidth,
            '--input-max-height': inputMaxHeight,
            '--input-margin-bottom': inputMarginBottom,
            width: '100%',
          } as CSSProperties
        }
        {...attributes}
      >
        <SearchInput
          alignment={inputAlignment}
          onChange={onSearchQueryChange}
          isVisible={!!inputEnabled}
          setIsSearching={setIsSearching}
          handleNonDebouncedSearchQuery={() => {}}
          handleResetSearchState={() => setShownData(defaultShownData)}
        />
        <div
          className={cn(
            'relative w-full h-fit',
            className,
            styles.rssFeedGrid,
            displayedShouldScrollHorizontally ? 'flex' : 'grid',
            !displayedShouldScrollHorizontally && COLUMNS_MAP[effectiveColumns || '3'],
          )}
          style={
            {
              '--gap': gap || '16px',
              padding: padding,

              // Desktop Slider
              '--desktop-slide-size': desktopSlideSize,
              '--post-slider-control-background-color': displayedSliderControlsBackgroundColor,
              '--post-slider-control-text-color': displayedSliderControlsTextColor,
              '--post-slider-control-border-color': displayedSliderControlsBorderColor,

              // Mobile Slider
              '--mobile-slider-control-background-color':
                element.attrs.mobileSliderControlsBackgroundColor || displayedSliderControlsBackgroundColor,
              '--mobile-slider-control-text-color':
                element.attrs.mobileSliderControlsTextColor || displayedSliderControlsTextColor,
              '--mobile-slider-control-border-color':
                element.attrs.mobileSliderControlsBorderColor || displayedSliderControlsBorderColor,
            } as CSSProperties
          }
        >
          <EmblaCarousel
            options={OPTIONS}
            isCarousel={!!displayedShouldScrollHorizontally}
            isLive={isLive}
            controlsDisplayType={displayedSliderControlsDisplayType}
          >
            {shownData.map((entry) => (
              <YouTubeRssFeedCard
                isLive={isLive}
                key={entry.id}
                entry={entry}
                element={element}
                attributes={attributes}
                isPreviewing={isPreviewing}
                onOpenModal={(videoId) => {
                  setModalVideoId(videoId);
                  setIsModalOpen(true);
                }}
              />
            ))}
          </EmblaCarousel>
          {children}
        </div>

        {!isSearching && (!shownData || shownData.length === 0) ? (
          <EmptyState
            padding={emptyStatePadding}
            backgroundColor={emptyStateBackgroundColor}
            textColor={emptyStateTextColor}
            fontSize={emptyStateFontSize}
          >
            {emptyStateText}
          </EmptyState>
        ) : null}
      </div>

      <Dialog
        open={isModalOpen}
        onOpenChange={(value) => {
          setIsModalOpen(value);
          if (!value) {
            setModalVideoId('');
          }
        }}
      >
        <DialogPortal>
          <DialogOverlay />
          <DialogContent className="top-[50%] translate-y-[-50%]">
            {isModalOpen && (
              <iframe
                width="560"
                height="315"
                src={`https://www.youtube.com/embed/${modalVideoId}`}
                title="YouTube video player"
                frameBorder="0"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            )}
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  );
};
